import { useState } from "react";
import { useForm, ValidationError } from "@formspree/react";
import { toast } from "sonner";
import { useReveal } from "@/lib/useReveal";
import { ArrowRight } from "lucide-react";

const BG_VIDEO = `${process.env.PUBLIC_URL}/videos/hero.mp4`;

export default function GetInvolved() {
  const ref = useReveal();
  const [state, handleSubmit] = useForm("mwvyzavz");
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    interest: "Partnership",
    message: "",
  });

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please share your name, email, and a short message.");
      return;
    }
    await handleSubmit(e);
  };

  // Show success after Formspree confirms
  if (state.succeeded) {
    return (
      <section
        id="contact"
        ref={ref}
        data-testid="contact-section"
        className="relative overflow-hidden bg-[#0F1B2D] text-white py-28 md:py-40 lg:py-48"
      >
        <div className="absolute inset-0 -z-0 opacity-[0.18]">
          <video autoPlay muted loop playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover">
            <source src={BG_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-[#0F1B2D]/75" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-display text-[clamp(2.5rem,6vw,6.25rem)] leading-[0.95] tracking-[-0.04em]">
            Thank you.
          </h2>
          <p className="mt-6 text-[17px] text-white/70">The Blude TechX team will be in touch.</p>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      ref={ref}
      data-testid="contact-section"
      className="relative overflow-hidden bg-[#0F1B2D] text-white py-28 md:py-40 lg:py-48"
    >
      {/* Background video */}
      <div className="absolute inset-0 -z-0 opacity-[0.18]">
        <video autoPlay muted loop playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover">
          <source src={BG_VIDEO} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-[#0F1B2D]/75" />
      </div>

      <div className="pointer-events-none absolute -right-32 -top-32 h-[420px] w-[420px] rounded-full bg-[#D62828]/15 blur-[120px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-white/55 reveal">
          <span className="inline-block h-px w-10 bg-[#D62828]" />
          Get Involved
        </div>

        <h2
          data-testid="contact-headline"
          className="mt-8 max-w-5xl font-display text-[clamp(2.5rem,6vw,6.25rem)] leading-[0.95] tracking-[-0.04em] reveal"
        >
          Let's build the{" "}
          <span className="font-serif-italic italic text-[#D62828]">future of health</span>{" "}
          together.
        </h2>

        <div className="mt-16 md:mt-24 grid grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="col-span-12 lg:col-span-7 reveal"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
              <Field label="Full Name" name="name" testid="contact-name" value={form.name} onChange={onChange("name")} />
              <ValidationError field="name" errors={state.errors} className="text-[#D62828] text-[12px]" />
              <Field label="Email" name="email" type="email" testid="contact-email" value={form.email} onChange={onChange("email")} />
              <ValidationError field="email" errors={state.errors} className="text-[#D62828] text-[12px]" />
              <Field label="Organization" name="organization" testid="contact-org" value={form.organization} onChange={onChange("organization")} />
              <Select label="I'm interested in" name="interest" testid="contact-interest" value={form.interest} onChange={onChange("interest")}
                options={["Partnership", "Clinical Pilot", "Investment", "Careers", "Press / Media", "Other"]}
              />
            </div>
            <Field label="Message" name="message" testid="contact-message" value={form.message} onChange={onChange("message")} textarea />
            <ValidationError field="message" errors={state.errors} className="text-[#D62828] text-[12px]" />

            <button
              type="submit"
              disabled={state.submitting}
              data-testid="contact-submit"
              className="mt-10 group inline-flex items-center gap-3 rounded-full bg-[#D62828] text-white pl-7 pr-2 py-2 text-[14px] font-semibold tracking-wide hover:bg-[#A4161A] transition-colors disabled:opacity-60"
            >
              {state.submitting ? "Sending…" : "Reach Out"}
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white text-[#111111] group-hover:translate-x-1 transition-transform duration-500">
                <ArrowRight size={16} />
              </span>
            </button>
          </form>

          {/* Side info */}
          <aside data-testid="contact-aside" className="col-span-12 lg:col-span-5 reveal">
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-md p-8 md:p-10">
              <div className="text-[11px] uppercase tracking-[0.24em] text-white/55">Headquarters</div>
              <div className="mt-3 font-display text-2xl tracking-tight">Madurai · Tamil Nadu</div>
              <div className="mt-2 text-[14px] text-white/65">Operating from South India · Version One Region</div>
              <div className="mt-8 grid grid-cols-2 gap-6">
                <Info label="Founded" value="2025" />
                <Info label="Structure" value="LLP" />
                <Info label="Domain" value="Digital Health" />
                <Info label="Focus" value="Evidence-led" />
              </div>
              <div className="mt-8 pt-6 border-t border-white/10 text-[13px] text-white/60">
                For partnerships, pilots, or media - write to us through the form. We respond within two working days.
              </div>
            </div>
            <button
              data-testid="view-services"
              onClick={() => { const el = document.getElementById("build"); if (el) el.scrollIntoView({ behavior: "smooth" }); }}
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3.5 text-[13px] font-semibold text-white hover:bg-white hover:text-[#0F1B2D] transition-colors"
            >
              View Services
            </button>
          </aside>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, testid, value, onChange, type = "text", textarea }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className={`block group ${textarea ? "md:col-span-2 mt-2" : ""}`}>
      <span className="text-[11px] uppercase tracking-[0.24em] text-white/55">{label}</span>
      <Comp
        data-testid={testid}
        name={name}
        type={type}
        rows={textarea ? 4 : undefined}
        value={value}
        onChange={onChange}
        className="mt-3 w-full bg-transparent border-b border-white/15 focus:border-[#D62828] outline-none py-3 text-[15px] text-white placeholder-white/40 transition-colors resize-none"
        placeholder=" "
      />
    </label>
  );
}

function Select({ label, name, testid, value, onChange, options }) {
  return (
    <label className="block">
      <span className="text-[11px] uppercase tracking-[0.24em] text-white/55">{label}</span>
      <div className="relative">
        <select
          data-testid={testid}
          name={name}
          value={value}
          onChange={onChange}
          className="mt-3 w-full bg-transparent border-b border-white/15 focus:border-[#D62828] outline-none py-3 text-[15px] text-white appearance-none"
        >
          {options.map((o) => (
            <option key={o} value={o} className="bg-[#0F1B2D] text-white">{o}</option>
          ))}
        </select>
        <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-white/50">{"▾"}</span>
      </div>
    </label>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-white/50">{label}</div>
      <div className="mt-1 font-display text-lg text-white">{value}</div>
    </div>
  );
}
